/**
 * Phase 8 — Cost Position card (project Overview tab).
 * Budget − Committed − Actual from /procurement/committed-cost.
 */
import React, { useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatINR } from '../../lib/format';

export default function CommittedCostCard({ projectId }) {
  const [cc, setCc] = useState(null);

  useEffect(() => {
    apiGet('/procurement/committed-cost', { project_id: projectId }).then(setCc).catch(() => {});
  }, [projectId]);

  if (!cc || (!cc.budget && !cc.committed && !cc.actual)) return null;

  const pct = cc.budget > 0 ? Math.min(100, Math.round(cc.committed / cc.budget * 100)) : 0;
  const over = cc.budget > 0 && cc.committed > cc.budget;

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 mt-4" data-testid="committed-cost-card">
      <div className="kpi-overline flex items-center gap-1.5 mb-3"><Wallet size={11} /> Cost Position · Budget vs Committed vs Actual</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Kpi label="Budget" value={formatINR(cc.budget)} />
        <Kpi label="Committed" value={formatINR(cc.committed)} red={over} />
        <Kpi label="Actual (paid/billed)" value={formatINR(cc.actual)} />
        <Kpi label="Available Budget" value={formatINR(cc.available_budget)} red={cc.available_budget < 0} />
        <Kpi label="Forecast at Completion" value={formatINR(cc.forecast_at_completion)} />
      </div>
      {cc.budget > 0 && (
        <div className="mt-3">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${over ? 'bg-red-500' : 'bg-[#3b4872]'}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] text-slate-500 mt-1">{pct}% of budget committed</div>
        </div>
      )}
      {(cc.by_category || []).length > 0 && (
        <table className="w-full dense-table mt-3">
          <thead><tr><th>Category</th><th className="text-right">Committed</th><th className="text-right">Actual</th></tr></thead>
          <tbody>
            {cc.breakdown?.material_pos > 0 && (
              <tr><td className="text-xs">Material POs</td><td className="text-right tabular-nums text-xs">{formatINR(cc.breakdown.material_pos)}</td><td className="text-right tabular-nums text-xs">—</td></tr>
            )}
            {cc.by_category.map((c, i) => (
              <tr key={i}>
                <td className="text-xs">{c.category}</td>
                <td className="text-right tabular-nums text-xs">{formatINR(c.committed)}</td>
                <td className="text-right tabular-nums text-xs">{formatINR(c.actual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const Kpi = ({ label, value, red }) => (
  <div>
    <div className="kpi-overline">{label}</div>
    <div className={`font-display font-bold text-base mt-0.5 ${red ? 'text-red-600' : 'text-slate-900'}`}>{value}</div>
  </div>
);
